<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import DownloadModal from "@/components/explorer/options/DownloadModal.vue";
import ShareModal from "@/components/explorer/options/ShareModal.vue";
import CitationModal from "@/components/explorer/options/CitationModal.vue";

	//properties set by parent
	const props = defineProps({
		chartObj: {
			type: Object,
			required: false
		},
		chartType: {
			type: String,
			required: true
		},
		indicator: {
			type: Object,
			required: false
		}
	})

	//componenet methods exposed to parent


	//reactive field proxies
	const showDownloadModal = ref(false)
	const showShareModal = ref(false)
	const showCitationModal = ref(false)

	//binding to invoke method on child componenet
	const childRefShareModal = ref({})
	const childRefOptionsMenu = ref({})
	const childRefOptionsMenuToggle = ref({})
	const childRefCitationModal = ref({})

	//defineModel makes a two-way binding
	const showNoDataWarning = defineModel({ type: Boolean, default: false })

	//local variables
	const isSmallScreen = () => window.innerWidth <= 1279;

	//methods
	function optionsMenuEventListener(e) {
		if (childRefOptionsMenu.value != null && !(childRefOptionsMenu.value.contains(e.target) ||
			childRefOptionsMenuToggle.value.contains(e.target))){
			toggleMenu();
		}
	}

	function closeCitationModal() {
		showCitationModal.value = false
	}

	function closeDownloadModal() {
		showDownloadModal.value = false
	}

	function closeShareModal() {
		showShareModal.value = false
	}

	function openCiteModal() {
		if(isSmallScreen) toggleMenu()
		showCitationModal.value = true
		childRefCitationModal.value.openModal()
	}

	function openDownloadModal() {
		if(isSmallScreen) toggleMenu()
		showDownloadModal.value = true
	}

	function openShareURLModal() {
		if(isSmallScreen) toggleMenu()
		showShareModal.value = true
		childRefShareModal.value.openModal()
	}

	function toggleMenu() {
		childRefOptionsMenu.value.classList.toggle('active');
		if(childRefOptionsMenu.value.classList.contains('active')) {
			document.addEventListener('click', optionsMenuEventListener);
			window.scrollTo(0, 0);
			childRefOptionsMenu.value.children[0].children[0].focus()
		} else {
			document.removeEventListener('click', optionsMenuEventListener);
		}
	}

	function onKeydown(event) {
		let currentIndex = -1
		const links = childRefOptionsMenu.value.children;
		for(let x = 0; x < links.length; x++) {
			if(links[x].children[0] == document.activeElement) {
				currentIndex = x;
				break;
			}
		}

		if (isSmallScreen()) {
			// Vertical navigation
			if (event.key === "ArrowDown") {
				event.preventDefault();
				links[(currentIndex + 1) % links.length].children[0]?.focus();
			}
			if (event.key === "ArrowUp") {
				event.preventDefault();
				links[(currentIndex - 1 + links.length) % links.length].children[0]?.focus();
			}

			// Focus trap
			if (event.key === "Tab") {
				if (!event.shiftKey && document.activeElement === links[links.length - 1]) {
					event.preventDefault();
					links[0].children[0].children[0]?.focus();
				}
			if (event.shiftKey && document.activeElement === links[0]) {
				event.preventDefault();
				links[links.length - 1].children[0]?.focus();
			}
		}
	} else {
		// Horizontal navigation
		if (event.key === "ArrowRight") {
			event.preventDefault();
			links[(currentIndex + 1) % links.length].children[0]?.focus();
		}
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			links[(currentIndex - 1 + links.length) % links.length].children[0]?.focus();
		}
	}
}

function onEscape(event) {
  if (event.key === "Escape" && isSmallScreen) {
	if(childRefOptionsMenu.value.classList.contains('active')) {
    	toggleMenu();
    	childRefOptionsMenuToggle.value.focus();
	}
  }
}

function closeMenu() {
	if(childRefOptionsMenu.value.classList.contains('active')) {
    	toggleMenu(false);
    	childRefOptionsMenuToggle.value.focus();
		document.removeEventListener('click', optionsMenuEventListener);
	}
}

onMounted(() => {
  document.addEventListener("keydown", onEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onEscape);
});
</script>

<template>
	<div id="chart-options">
		<div id="chart-options-shell">
			<div class="chart-options-row" ref="childRefOptionsMenu" @keydown="onKeydown">
				<div class="chart-options-close">
					<button class="btn-icon btn-close" @click="closeMenu" aria-label="Close"><i class="fa-solid fa-times"></i></button>
				</div>
				<div class="chart-options-col">
					<button class="btn-tertiary" :disabled="showNoDataWarning" @click="openCiteModal" aria-label="Open CITE modal"><i class="fa-solid fa-message-quote"></i> Cite</button>
				</div>
				<div class="chart-options-col">
					<button class="btn-tertiary" :disabled="showNoDataWarning" @click="openShareURLModal" aria-label="Open share URL modal"><i class="fa-solid fa-share"></i> Share</button>
				</div>
				<div class="chart-options-col">
					<button class="btn-tertiary" @click="openDownloadModal" aria-label="Open download modal" :disabled="props.chartType == 'source'||showNoDataWarning"><i class="fa-solid fa-download"></i> Download</button>
				</div>
			</div>
		</div>
		<button id="chart-options-menu-toggle" ref="childRefOptionsMenuToggle" title="Explorer options collaped button" tabindex="0" @click="toggleMenu" @keydown.enter.prevent="toggleMenu">
			<i class="fa-regular fa-ellipsis"></i>
		</button>
	</div>
	<CitationModal  :isOpen="showCitationModal" :indicator="indicator" ref="childRefCitationModal"  @closeCitationModal="closeCitationModal" />
  <ShareModal :isOpen="showShareModal" :indicator="indicator" ref="childRefShareModal"  @closeShareModal="closeShareModal" />
	<DownloadModal :isOpen="showDownloadModal" :chartObj="chartObj" :indicator="indicator" :chartType="props.chartType" @closeDownload="closeDownloadModal" />
</template>

<style>
/* 499px - Mobile phone */
@media (max-width: 31.1875rem) {
	body:has(.chart-options-row.active) {
		overflow-y: hidden;
		height: 100vh;
	}
}
</style>
<style scoped>
#chart-options {
	margin-left: 1.5rem;
	position: relative;
}

.chart-options-row {
	display:flex;
	flex-direction: row;
	flex-wrap: nowrap;
	column-gap: var(--spacing-lg);
}

#chart-options-menu-toggle {
	display:none;
	border:0;
	padding: 0;
	background-color: transparent;
	min-width: 2.5rem;
}

/*
.chart-options-col {
	display: flex;
	flex-direction: column;
	justify-content: start;
}
*/

.chart-options-col .btn-tertiary{
	text-wrap: nowrap;
	border-radius: 0;
}

.chart-options-col .btn-tertiary:disabled {
	border:0;
	cursor: default;
	text-decoration: none;
}

.chart-options-close {
	display: none;
}

.btn-close {
	color: var(--icon-cta-Blue, #064291);
	text-align: right;
	font-weight: 900;
	line-height: normal;
	font-size: var(--font-size-H4);
	min-width: 1rem;
}

/* 1279px - Small laptop */
/* reduced this to 1024px threshold */
@media only screen and (max-width: 64rem) {
	.chart-options-row {
		flex-direction: column;
		display: none;
		position: absolute;
		right: 0;
		top: 2.5rem;
		background: #fff;
        padding: 0;
        z-index: 2;
        border: 1px solid var(--border-color-input-Border, #A9B7C7);
	}

	.chart-options-row.active {
		display:flex;
	}

	#chart-options-menu-toggle {
		display:block;
		padding: 0 0.5rem;
		height: 2.5rem;
	}

	.chart-options-col{
		height: 2.5rem;
		min-width:unset;
		width: 100%;
		padding: 0;
		border-bottom: 1px solid var(--border-color-input-Border, #A9B7C7);
	}

	.chart-options-col .btn-tertiary{
		padding: 0.5rem 1rem;
		height: 2.5rem;
		min-width:unset;
		width: 100%;
		text-align: left;
		color: var(--text-Primary);
	}

	.chart-options-col .btn-tertiary i{
		display:none;
	}

	.chart-options-col .btn-tertiary:hover{
		background: var(--surface-color-Tertiary, #FAFAFA);
		border-bottom: 1px solid var(--border-color-input-Border, #A9B7C7);
	}

	.chart-options-col .btn-tertiary:focus{
		border: 1.5px solid var(--border-color-divider-Active, #064291);
		outline: 0;
	}
}

/* 499px - Mobile phone */
@media (max-width: 31.1875rem) {
	/* technique to left align dropdown */
	#chart-options-shell {
		max-width: 500px;
		margin: 0 auto;
        position: fixed;
        left: 0;
        width: 100%;
        z-index: 999;
	}

	.chart-options-row {
		margin: -1rem 0 0 0;
		width: 100%;
		text-align: left;
		position: sticky;
		left: 0;
		top: 0;
		height: calc(100vh);
		width: 100vw;
		margin-left: calc(-50vw + 50%);
	}

	.chart-options-col{
		height: unset;
	}

	.chart-options-col .btn-tertiary{
		padding: var(--spacing-xl);
		height: 76px;
		font-size: var(--font-size-H4)
	}

	.chart-options-close {
		display: flex;
		width: 100%;
		height: 4.75rem;
		justify-content: end;
		align-items: center;
		padding: 1.25rem;
		border-bottom: 1px solid var(--border-color-Divider, #A9B7C7);
	}

/* 	.viz-menu-container-dropdown button:last-child {
		border-bottom: 1px solid var(--border-color-Divider, #A9B7C7);
	} */
}
</style>
